/**
 * Cash Flow Context.
 *
 * Loads and exposes the four collections that drive the cashflow page:
 *   - bank_accounts            (seeded with Business + Everyday)
 *   - bank_transactions
 *   - bank_import_batches
 *   - bank_rules               (seeded from utils/categories.ts on first read)
 *
 * Actions:
 *   - commitImport(preview)    — writes the new transactions + batch doc,
 *                                then runs transfer-pairing across the
 *                                full unpaired set.
 *   - undoImport(batchId)      — deletes the transactions added by a
 *                                batch, flips the batch doc to reverted.
 *   - setCategory(txId, cat)   — manual override with sticky flag.
 *   - addRule / updateRule / deleteRule
 *
 * Imports and reversals are server-authoritative. A single live subscription
 * keeps transaction rows current. Classification remains a separate client-side
 * concern, constrained by Firestore field-level rules.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  getFirestore,
  query,
  orderBy,
  onSnapshot,
  setDoc,
  writeBatch,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import {
  BankAccount,
  BankTransaction,
  BankImportBatch,
  BankRule,
  ImportPreview,
} from '../types';
import { SEED_RULES, SEED_RULES_V2, SEED_RULES_V3, SEED_RULES_V4, SEED_RULES_V5 } from '../utils/categories';
import { findTransferPairs, extractTransferRef } from '../utils/transferPairing';
import { classify } from '../utils/rulesEngine';
import { TxScope } from '../types';
import { bankRequest } from '../reconciliationApi';

const ACCOUNTS_COLL = 'bank_accounts';
const TRANSACTIONS_COLL = 'bank_transactions';
const BATCHES_COLL = 'bank_import_batches';
const RULES_COLL = 'bank_rules';

/** Default accounts seeded on first visit. The user can rename/edit
 *  via the future Accounts editor (out of scope for v1 — for now they
 *  can edit directly in Firestore if needed). */
const SEED_ACCOUNTS: Array<Omit<BankAccount, 'createdAt' | 'updatedAt'>> = [
  { id: 'anz-business', displayName: 'ANZ Business',  type: 'business',  bankAccountTail: '015237280730859', defaultScope: 'business' },
  { id: 'anz-everyday', displayName: 'ANZ Everyday',  type: 'everyday',  bankAccountTail: '300571075',       defaultScope: 'personal' },
  // Mixed-use card: NO defaultScope on purpose — Anthropic subscriptions
  // and house flooring live on the same statement, so unmatched rows go
  // to the Unassigned queue for explicit triage instead of silently
  // landing in one set of books.
  { id: 'coles-credit-card', displayName: 'Coles Credit Card', type: 'credit-card' },
  // CommBank household account — receives family payments, pays the mortgage.
  // Personal books by default (the deductible mortgage-interest share is
  // claimed separately in the tax view, not as a transaction).
  { id: 'cba-everyday', displayName: 'CBA Everyday', type: 'everyday', defaultScope: 'personal' },
];

interface CashFlowState {
  accounts: BankAccount[];
  transactions: BankTransaction[];
  batches: BankImportBatch[];
  rules: BankRule[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
}

interface CashFlowActions {
  refresh: () => Promise<void>;
  commitImport: (preview: ImportPreview) => Promise<{ batchId: string; insertedCount: number }>;
  undoImport: (batchId: string) => Promise<void>;
  setCategory: (txId: string, category: string, opts?: { subcategory?: string }) => Promise<void>;
  /** Manually assign business/family books (sticky — rule re-runs
   *  won't overwrite). null clears back to unassigned. */
  setScope: (txId: string, scope: TxScope | null) => Promise<void>;
  /** Bulk manual classification — batched writes with live row updates.
   *  Category and/or scope; both are sticky. Returns
   *  the number of transactions written. */
  bulkCategorise: (txIds: string[], patch: { category?: string; scope?: TxScope }) => Promise<number>;
  linkInvoice: (txId: string, invoiceId: string | null) => Promise<void>;
  addRule: (rule: Omit<BankRule, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateRule: (id: string, patch: Partial<BankRule>) => Promise<void>;
  deleteRule: (id: string) => Promise<void>;
  /** Re-run the rules engine against every transaction that the user
   *  hasn't manually categorised. Returns counts so the caller can
   *  toast 'Updated X / Y transactions'. Internal transfers and
   *  user-categorised rows are left alone. */
  reclassifyAll: () => Promise<{ updated: number; scanned: number }>;
  /** Check whether a hash already exists. Used by the preview pass so
   *  the user sees "X new / Y duplicate" before they confirm. */
  checkExistingIds: (ids: string[]) => Promise<Set<string>>;
}

type ContextValue = CashFlowState & CashFlowActions;

const CashFlowContext = createContext<ContextValue | null>(null);

/** Split an array into chunks. Used to stay under Firestore's 500-op
 *  batched-write limit. */
function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function CashFlowProvider({ children }: { children: ReactNode }) {
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [batches, setBatches] = useState<BankImportBatch[]>([]);
  const [rules, setRules] = useState<BankRule[]>([]);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [feedError, setFeedError] = useState<string | null>(null);
  const [feedRevision, setFeedRevision] = useState(0);
  const feedFailed = useRef(false);
  const loading = metadataLoading || transactionsLoading;
  const seeded = useRef(false);
  const refreshFlight = useRef<Promise<void> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // One subscription for this page lifetime. Changes preserve the existing list
  // and row identities; ordinary writes never replace the feed with a spinner.
  useEffect(() => onSnapshot(
    query(collection(getFirestore(), TRANSACTIONS_COLL), orderBy('date', 'desc')),
    snapshot => {
      const changed = new Set(snapshot.docChanges().map(change => change.doc.id));
      setTransactions(previous => {
        const existing = new Map(previous.map(t => [t.id, t]));
        return snapshot.docs.map(d => !changed.has(d.id) && existing.has(d.id)
          ? existing.get(d.id)!
          : {...d.data(), id: d.id} as BankTransaction);
      });
      setFeedError(null);
      feedFailed.current = false;
      setTransactionsLoading(false);
    },
    err => {
      feedFailed.current = true;
      setFeedError(err.message || 'Bank transactions could not update');
      setTransactionsLoading(false);
    },
  ), [feedRevision]);

  /** Ensure the seed accounts + seed rules exist. Idempotent —
   *  re-seeding is safe because we use setDoc with deterministic ids. */
  const ensureSeeds = useCallback(async () => {
    const db = getFirestore();
    // Accounts: only write if the doc doesn't already exist (so the
    // user's edits aren't clobbered on every page load). One exception:
    // backfill defaultScope onto pre-scope-era account docs that don't
    // have the field yet (added 2026-06) — merge-only, never overwrites
    // a value the user has set.
    for (const a of SEED_ACCOUNTS) {
      const ref = doc(db, ACCOUNTS_COLL, a.id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        await setDoc(ref, { ...a, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
      } else if (a.defaultScope && snap.data()?.defaultScope === undefined) {
        await setDoc(ref, { defaultScope: a.defaultScope, updatedAt: serverTimestamp() }, { merge: true });
      }
    }
    // Rules: seed once. The seed marker is an empty doc at
    // bank_rules/_seeded — its existence means we've already seeded
    // and shouldn't re-add (the user may have deleted some seeds
    // intentionally).
    const marker = doc(db, RULES_COLL, '_seeded');
    const markerSnap = await getDoc(marker);
    if (!markerSnap.exists()) {
      const batch = writeBatch(db);
      for (let i = 0; i < SEED_RULES.length; i++) {
        const r = SEED_RULES[i];
        const id = `seed_${i.toString().padStart(3, '0')}`;
        batch.set(doc(db, RULES_COLL, id), {
          ...r,
          id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      batch.set(marker, { seededAt: serverTimestamp() });
      await batch.commit();
    }
    // Second seed wave (Coles CC + scope rules) under its own marker so
    // installs that consumed v1 pick these up exactly once too.
    const markerV2 = doc(db, RULES_COLL, '_seeded_v2');
    const markerV2Snap = await getDoc(markerV2);
    if (!markerV2Snap.exists()) {
      const batch = writeBatch(db);
      for (let i = 0; i < SEED_RULES_V2.length; i++) {
        const r = SEED_RULES_V2[i];
        const id = `seed_v2_${i.toString().padStart(3, '0')}`;
        batch.set(doc(db, RULES_COLL, id), {
          ...r,
          id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      batch.set(markerV2, { seededAt: serverTimestamp() });
      await batch.commit();
    }
    // Third seed wave (2026-07): full merchant → category pack from the ANZ
    // business + everyday statements. Own marker so it seeds exactly once.
    const markerV3 = doc(db, RULES_COLL, '_seeded_v3');
    const markerV3Snap = await getDoc(markerV3);
    if (!markerV3Snap.exists()) {
      const batch = writeBatch(db);
      for (let i = 0; i < SEED_RULES_V3.length; i++) {
        const r = SEED_RULES_V3[i];
        const id = `seed_v3_${i.toString().padStart(3, '0')}`;
        batch.set(doc(db, RULES_COLL, id), {
          ...r,
          id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      batch.set(markerV3, { seededAt: serverTimestamp() });
      await batch.commit();
    }
    // Fourth seed wave (2026-07): CommBank/CBA rules.
    const markerV4 = doc(db, RULES_COLL, '_seeded_v4');
    const markerV4Snap = await getDoc(markerV4);
    if (!markerV4Snap.exists()) {
      const batch = writeBatch(db);
      for (let i = 0; i < SEED_RULES_V4.length; i++) {
        const r = SEED_RULES_V4[i];
        const id = `seed_v4_${i.toString().padStart(3, '0')}`;
        batch.set(doc(db, RULES_COLL, id), {
          ...r,
          id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      batch.set(markerV4, { seededAt: serverTimestamp() });
      await batch.commit();
    }
    // Fifth seed wave (2026-07): non-assessable gov payments + CBA transfers.
    const markerV5 = doc(db, RULES_COLL, '_seeded_v5');
    const markerV5Snap = await getDoc(markerV5);
    if (!markerV5Snap.exists()) {
      const batch = writeBatch(db);
      for (let i = 0; i < SEED_RULES_V5.length; i++) {
        const r = SEED_RULES_V5[i];
        const id = `seed_v5_${i.toString().padStart(3, '0')}`;
        batch.set(doc(db, RULES_COLL, id), {
          ...r,
          id,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      batch.set(markerV5, { seededAt: serverTimestamp() });
      await batch.commit();
    }
  }, []);

  const refresh = useCallback((): Promise<void> => {
    if (feedFailed.current) {
      feedFailed.current = false;
      setFeedRevision(v => v + 1);
    }
    if (refreshFlight.current) return refreshFlight.current;
    const work = (async () => {
      setRefreshing(true);
      setError(null);
      try {
        const db = getFirestore();
        if (!seeded.current) {
          await ensureSeeds();
          seeded.current = true;
        }

        const [aSnap, bSnap, rSnap] = await Promise.all([
          getDocs(query(collection(db, ACCOUNTS_COLL))),
          getDocs(query(collection(db, BATCHES_COLL), orderBy('uploadedAt', 'desc'))),
          getDocs(query(collection(db, RULES_COLL))),
        ]);
        setAccounts(aSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setBatches(
          bSnap.docs
            .filter((d) => d.id !== '_seeded')
            .map((d) => ({ id: d.id, ...(d.data() as any) })),
        );
        setRules(
          rSnap.docs
            .filter((d) => !d.id.startsWith('_seeded'))
            .map((d) => ({ id: d.id, ...(d.data() as any) }))
            // Stable sort: priority desc, then id for tie-break.
            .sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0) || a.id.localeCompare(b.id)),
        );
      } catch (e: any) {
        setError(e?.message || 'Failed to load cashflow data');
      } finally {
        setMetadataLoading(false);
        setRefreshing(false);
      }
    })();
    refreshFlight.current = work;
    void work.finally(() => { if (refreshFlight.current === work) refreshFlight.current = null; });
    return work;
  }, [ensureSeeds]);

  // An import/rule write must not reuse a metadata read that began before it committed.
  const refreshAfterWrite = useCallback(async () => {
    await refreshFlight.current;
    await refresh();
  }, [refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Look up which of the given hash-ids already exist in
   *  bank_transactions. Used by the upload preview to count
   *  duplicates without committing.
   *
   *  Firestore's `where('__name__', 'in', [...])` caps at 30 ids per
   *  query (10 before recent SDK updates) — we batch. For larger
   *  preview sets we fall through to a per-doc getDoc which is slower
   *  but unbounded. */
  const checkExistingIds = useCallback(async (ids: string[]): Promise<Set<string>> => {
    const existing = new Set<string>();
    if (!ids.length) return existing;
    const db = getFirestore();
    const collRef = collection(db, TRANSACTIONS_COLL);
    const groups = chunk(ids, 30);
    for (const g of groups) {
      try {
        const snap = await getDocs(query(collRef, where('__name__', 'in', g)));
        snap.docs.forEach((d) => existing.add(d.id));
      } catch {
        // Fall back to per-doc reads if the 'in' query fails for any
        // reason (extreme edge case).
        for (const id of g) {
          const s = await getDoc(doc(db, TRANSACTIONS_COLL, id));
          if (s.exists()) existing.add(id);
        }
      }
    }
    return existing;
  }, []);

  /** Server-side create-only chunks preserve existing evidence on overlapping imports. */
  const commitImport = useCallback(async (preview: ImportPreview): Promise<{ batchId: string; insertedCount: number }> => {
    const db = getFirestore();
    // Keep a retry of this same preview on the same import, including partial uploads.
    const batchId = (preview as any).batchId || ((preview as any).batchId = 'imp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
    const groups = chunk(preview.newTransactions, 150);
    if (!groups.length) groups.push([]);
    let insertedCount = 0;
    for (let part = 0; part < groups.length; part++) {
      const committed = await bankRequest('importChunk', { batchId, part, parts: groups.length, rows: groups[part], meta: {
        accountId: preview.accountId, fileName: preview.fileName, fileHash: preview.fileHash,
        rowCount: preview.totalRows, skippedDuplicateCount: preview.duplicateIds.length,
        dateRangeStart: preview.dateRangeStart, dateRangeEnd: preview.dateRangeEnd,
      } });
      insertedCount += committed.inserted;
    }

    // 3. Pull every unpaired transaction across all accounts and run
    //    a pairing pass. We could be smarter (only candidates near
    //    the new rows' date range) but at the volume the user runs
    //    monthly, this stays well under a few hundred reads.
    const unpairedSnap = await getDocs(
      query(collection(db, TRANSACTIONS_COLL), where('category', '==', 'uncategorised')),
    );
    const unpaired: BankTransaction[] = unpairedSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    // Read persisted rows: an overlapping import must never put a stale
    // preview back over an existing classification or reconciliation.
    const importedSnap = await getDocs(query(collection(db, TRANSACTIONS_COLL), where('importBatchId', '==', batchId)));
    const seenIds = new Set(unpaired.map((t) => t.id));
    for (const row of importedSnap.docs) {
      if (!seenIds.has(row.id)) unpaired.push({ ...row.data(), id: row.id } as BankTransaction);
    }
    const eligible = (t: BankTransaction) => !t.reconciliationId && !t.reviewedAt && !t.userCategorised && !t.pairedTransactionId;
    const updates = findTransferPairs(unpaired.filter(eligible));
    // Pair both sides atomically, rechecking manual edits made during upload.
    for (const group of chunk(updates, 2)) {
      await runTransaction(db, async (tx) => {
        const snapshots = await Promise.all(group.map(u => tx.get(doc(db, TRANSACTIONS_COLL, u.id))));
        const current = snapshots.filter(s => s.exists()).map(s => ({...s.data(), id: s.id} as BankTransaction));
        if (current.length !== 2 || !current.every(eligible) || findTransferPairs(current).length !== 2) return;
        for (const u of group) {
          tx.update(doc(db, TRANSACTIONS_COLL, u.id), {
            category: u.category,
            transferGroupId: u.transferGroupId,
            pairedTransactionId: u.pairedTransactionId,
            updatedAt: serverTimestamp(),
          });
        }
      });
    }

    await refreshAfterWrite();
    return { batchId, insertedCount };
  }, [refreshAfterWrite]);

  /** Server checks active reconciliations and cleans transfer links before deletion. */
  const undoImport = useCallback(async (batchId: string) => {
    await bankRequest('undoImport', { batchId });
    await refreshAfterWrite();
  }, [refreshAfterWrite]);

  const setCategory = useCallback(async (txId: string, category: string, opts?: { subcategory?: string }) => {
    const db = getFirestore();
    await setDoc(doc(db, TRANSACTIONS_COLL, txId), {
      category,
      subcategory: opts?.subcategory || null,
      userCategorised: true,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  const bulkCategorise = useCallback(async (txIds: string[], patch: { category?: string; scope?: TxScope }): Promise<number> => {
    if (!txIds.length || (!patch.category && !patch.scope)) return 0;
    const db = getFirestore();
    const fields: Record<string, any> = { updatedAt: serverTimestamp() };
    if (patch.category) {
      fields.category = patch.category;
      fields.subcategory = null;
      fields.userCategorised = true;
    }
    if (patch.scope) {
      fields.scope = patch.scope;
      fields.userScoped = true;
    }
    const groups = chunk(txIds, 450);
    for (const group of groups) {
      const wb = writeBatch(db);
      for (const id of group) {
        wb.set(doc(db, TRANSACTIONS_COLL, id), fields, { merge: true });
      }
      await wb.commit();
    }
    return txIds.length;
  }, []);

  const setScope = useCallback(async (txId: string, scope: TxScope | null) => {
    const db = getFirestore();
    await setDoc(doc(db, TRANSACTIONS_COLL, txId), {
      scope: scope || null,
      // Clearing back to unassigned also clears the sticky flag so the
      // next rule run may re-derive a scope; an explicit assignment is
      // sticky like manual categorisation.
      userScoped: !!scope,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  const linkInvoice = useCallback(async (txId: string, invoiceId: string | null) => {
    const db = getFirestore();
    await setDoc(doc(db, TRANSACTIONS_COLL, txId), {
      linkedInvoiceId: invoiceId,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  const addRule = useCallback(async (rule: Omit<BankRule, 'id' | 'createdAt' | 'updatedAt'>) => {
    const db = getFirestore();
    const id = `rule_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    await setDoc(doc(db, RULES_COLL, id), {
      ...rule,
      id,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await refreshAfterWrite();
  }, [refreshAfterWrite]);

  const updateRule = useCallback(async (id: string, patch: Partial<BankRule>) => {
    const db = getFirestore();
    await setDoc(doc(db, RULES_COLL, id), {
      ...patch,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await refreshAfterWrite();
  }, [refreshAfterWrite]);

  const deleteRule = useCallback(async (id: string) => {
    const db = getFirestore();
    const wb = writeBatch(db);
    wb.delete(doc(db, RULES_COLL, id));
    await wb.commit();
    await refreshAfterWrite();
  }, [refreshAfterWrite]);

  /** Walk through every transaction that ISN'T user-categorised, run
   *  the rules engine against it, and write back if the result differs
   *  from the stored category. Internal transfers are skipped
   *  unconditionally — they were paired deterministically, no rule
   *  should overwrite that. */
  const reclassifyAll = useCallback(async (): Promise<{ updated: number; scanned: number }> => {
    const db = getFirestore();
    // Pull fresh transactions + rules so we're not racing the
    // background state. (Slightly redundant with refresh() at the end
    // but ensures correctness if a sibling tab just wrote.)
    const [tSnap, rSnap, aSnap] = await Promise.all([
      getDocs(query(collection(db, TRANSACTIONS_COLL))),
      getDocs(query(collection(db, RULES_COLL))),
      getDocs(query(collection(db, ACCOUNTS_COLL))),
    ]);
    const txs: BankTransaction[] = tSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
    const currentRules: BankRule[] = rSnap.docs
      .filter((d) => !d.id.startsWith('_seeded'))
      .map((d) => ({ id: d.id, ...(d.data() as any) }));
    const accountScope = new Map<string, TxScope | undefined>(
      aSnap.docs.map((d) => [d.id, (d.data() as any).defaultScope]),
    );

    let scanned = 0;
    const writes: Array<{ id: string; patch: Record<string, any> }> = [];
    for (const t of txs) {
      // Scope is re-derived even for rows whose CATEGORY is settled
      // (user-categorised / paired transfers) — the dimensions are
      // independent and scope arrived later, so old rows need backfill.
      // A user-assigned scope (userScoped) is always left alone.
      const skipCategory =
        t.userCategorised
        || t.category === 'internal_transfer'
        // Unpaired transfer-likes stay in the uncategorised queue until
        // their sibling shows up — never rule-categorised.
        || !!extractTransferRef(t.descriptionRaw);

      const hit = classify(currentRules, t.accountId, t.descriptionRaw, t.descriptionCanonical || t.descriptionRaw.toLowerCase());
      const patch: Record<string, any> = {};

      if (!skipCategory) {
        scanned++;
        const nextCategory = hit?.category || 'uncategorised';
        const nextSub = hit?.subcategory || null;
        if (nextCategory !== t.category || (nextSub || null) !== (t.subcategory || null)) {
          patch.category = nextCategory;
          patch.subcategory = nextSub;
        }
      }

      if (!t.userScoped) {
        const nextScope = hit?.scope || accountScope.get(t.accountId) || null;
        if ((nextScope || null) !== (t.scope || null)) {
          patch.scope = nextScope;
        }
      }

      if (Object.keys(patch).length > 0) {
        writes.push({ id: t.id, patch });
      }
    }

    if (writes.length) {
      const groups = chunk(writes, 450);
      for (const group of groups) {
        const wb = writeBatch(db);
        for (const w of group) {
          wb.update(doc(db, TRANSACTIONS_COLL, w.id), {
            ...w.patch,
            updatedAt: serverTimestamp(),
          });
        }
        await wb.commit();
      }
    }

    await refreshAfterWrite();
    return { updated: writes.length, scanned };
  }, [refreshAfterWrite]);

  const value = useMemo<ContextValue>(() => ({
    accounts,
    transactions,
    batches,
    rules,
    loading,
    refreshing,
    error: feedError || error,
    refresh,
    commitImport,
    undoImport,
    setCategory,
    setScope,
    bulkCategorise,
    linkInvoice,
    addRule,
    updateRule,
    deleteRule,
    reclassifyAll,
    checkExistingIds,
  }), [accounts, transactions, batches, rules, loading, refreshing, feedError, error, refresh, commitImport, undoImport, setCategory, setScope, bulkCategorise, linkInvoice, addRule, updateRule, deleteRule, reclassifyAll, checkExistingIds]);

  return <CashFlowContext.Provider value={value}>{children}</CashFlowContext.Provider>;
}

export function useCashFlow(): ContextValue {
  const ctx = useContext(CashFlowContext);
  if (!ctx) throw new Error('useCashFlow must be used inside <CashFlowProvider>');
  return ctx;
}
